import { ApiError, uuid } from "@gjwtrack/client";
import type { NotificationPrefs } from "@gjwtrack/domain";
import { Copy, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { TopBar } from "../components/TopBar.tsx";
import { client, useClient } from "../lib/client.ts";
import { absTime, relTimeAgo } from "../lib/format.ts";
import { Button, Checkbox, Input } from "../ui/ring.ts";
import { copyText } from "../lib/copyIssue.ts";

type Token = { id: number; name: string; createdAt: number; lastUsedAt: number | null };
const PREF_LABELS: [keyof NotificationPrefs, string][] = [
  ["watched", "Issues I watch"],
  ["assigned", "Issues assigned to me"],
  ["reported", "Issues I reported"],
  ["mentioned", "When someone mentions me"],
  ["email", "Also send these by email"],
];

/** Profile: name, notification preferences, API tokens for the external REST API. */
export function AccountPage() {
  const me = useClient((s) => s.me);
  const navigate = useNavigate();
  const [name, setName] = useState(me?.name ?? "");
  const [prefs, setPrefs] = useState<NotificationPrefs>({});
  const [tokens, setTokens] = useState<Token[]>([]);
  const [newName, setNewName] = useState("");
  const [fresh, setFresh] = useState<{ name: string; token: string } | null>(null);
  const [pw, setPw] = useState({ current: "", next: "", again: "" });
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const changePassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (pw.next !== pw.again) {
      setPwMsg({ ok: false, text: "The new passwords do not match." });
      return;
    }
    try {
      await client.http.request("POST", "/auth/change-password", { current: pw.current, password: pw.next });
      setPw({ current: "", next: "", again: "" });
      setPwMsg({ ok: true, text: "Password changed. Your other sessions were signed out." });
    } catch (err) {
      setPwMsg({ ok: false, text: err instanceof ApiError && err.code === "wrong_password" ? "The current password is wrong." : (err as Error).message });
    }
  };
  const reload = useCallback(async () => {
    const [p, t] = await Promise.all([client.http.notificationPrefs(), client.http.apiTokens()]);
    setPrefs(p);
    setTokens(t);
  }, []);
  useEffect(() => {
    reload().catch((e) => client.pushError("account", (e as Error).message));
  }, [reload]);
  const run = async (cmd: string, input: unknown): Promise<unknown> => {
    try {
      return await client.http.command(cmd, input, uuid());
    } catch (e) {
      client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
      throw e;
    }
  };
  if (!me) return null;
  return (
    <div className="flex h-full flex-col">
      <TopBar crumbs={[{ label: "Account" }]} query="" onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")} onCreate={() => navigate("/issues")} />
      <div className="max-w-3xl flex-1 overflow-auto px-8 py-5">
        <h1 className="mb-4 text-xl font-semibold tracking-tight">{me.name}</h1>
        <section className="mb-8">
          <h2 className="mb-2 text-sm font-medium text-muted uppercase">Profile</h2>
          <div className="flex items-end gap-2">
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Button disabled={name.trim() === me.name || !name.trim()} onClick={() => void run("updateUser", { userId: me.id, name: name.trim() }).then(() => client.reloadMeta())}>
              Save
            </Button>
            <span className="pb-1.5 text-muted">{me.email}</span>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="mb-2 text-sm font-medium text-muted uppercase">Password</h2>
          <form className="flex max-w-md flex-col gap-2" onSubmit={(e) => void changePassword(e)}>
            <Input type="password" label="Current password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
            <Input type="password" label="New password (at least 8 characters)" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
            <Input type="password" label="Repeat new password" autoComplete="new-password" value={pw.again} onChange={(e) => setPw({ ...pw, again: e.target.value })} />
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={!pw.current || pw.next.length < 8 || !pw.again}>
                Change password
              </Button>
              {pwMsg && <span className={pwMsg.ok ? "text-ok" : "text-danger"}>{pwMsg.text}</span>}
            </div>
          </form>
        </section>

        <section className="mb-8">
          <h2 className="mb-2 text-sm font-medium text-muted uppercase">Notifications</h2>
          <div className="flex flex-col gap-1">
            {PREF_LABELS.map(([key, label]) => (
              <Checkbox key={key} label={label} checked={prefs[key] !== false} onChange={(e) => void run("setNotificationPrefs", { [key]: e.target.checked }).then(reload)} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-medium text-muted uppercase">API tokens</h2>
          <p className="mb-3 max-w-prose text-muted">
            For scripts and integrations. Use <code>Authorization: Bearer &lt;token&gt;</code> against <code>{location.origin}/yt/api/…</code> — the same paths, <code>fields=</code> projection and issue shapes as YouTrack's REST API.
          </p>
          {fresh && (
            <div className="mb-3 rounded border border-accent/40 bg-accent-soft p-3">
              <div className="mb-1 text-[13px]">Token <b>{fresh.name}</b> created. Copy it now; it is not shown again.</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-surface px-2 py-1 font-mono text-[13px] break-all select-all">{fresh.token}</code>
                <Button icon={Copy} title="Copy" onClick={() => void copyText(fresh.token)} />
              </div>
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr>
                {["Name", "Created", "Last used", ""].map((h) => (
                  <th key={h} className="border-b border-line px-3 py-1.5 text-left text-xs font-medium text-muted uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td className="border-b border-line px-3 py-1.5">{t.name}</td>
                  <td className="border-b border-line px-3 py-1.5 text-muted" title={absTime(t.createdAt)}>{relTimeAgo(t.createdAt)}</td>
                  <td className="border-b border-line px-3 py-1.5 text-muted">{t.lastUsedAt ? relTimeAgo(t.lastUsedAt) : "never"}</td>
                  <td className="border-b border-line px-3 py-1.5">
                    <Button icon={Trash2} title="Revoke" onClick={() => confirm(`Revoke token "${t.name}"?`) && void run("revokeApiToken", { tokenId: t.id }).then(reload)} />
                  </td>
                </tr>
              ))}
              {tokens.length === 0 && (
                <tr>
                  <td className="px-3 py-2 text-muted" colSpan={4}>No tokens.</td>
                </tr>
              )}
            </tbody>
          </table>
          <form
            className="mt-3 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!newName.trim()) return;
              void run("createApiToken", { name: newName.trim() }).then((r) => {
                setFresh(r as { name: string; token: string });
                setNewName("");
                return reload();
              });
            }}
          >
            <Input placeholder="Token name (e.g. CI)" value={newName} onChange={(e) => setNewName(e.target.value)} />
            <Button type="submit" icon={Plus} disabled={!newName.trim()}>
              New token
            </Button>
          </form>
        </section>
      </div>
    </div>
  );
}
